import { t } from "@lingui/macro";
import { MotiView } from "moti";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Animated,
  Modal,
  PanResponder,
  PanResponderInstance,
  TextInput,
  View,
  StyleSheet,
  Platform,
} from "react-native";

import {
  AnimationDurations,
  AnimationEasings,
} from "../../../../theme/animations";
import { Box, Text } from "../../../primitive";
import { getXValueFromPercent } from "../utils";
import { RangeSliderPercentValueMapItem } from "./../types";

//@todo figure out why ts doesn't like accessing _value... which is totally legit
//For now I've just told typescript to ignore the _ accessors

export const SliderKnob = ({
  sliderPixelWidth,
  currentPercent,
  setCurrentPercent,
  leftBoundaryPercent,
  rightBoundaryPercent,
  isRight,
  setHasFocus,
  currentValueFromMap,
  setIsDragging,
}: {
  sliderPixelWidth: number;
  currentPercent: number;
  leftBoundaryPercent: number;
  rightBoundaryPercent: number;
  currentValueFromMap?: RangeSliderPercentValueMapItem;
  isRight?: boolean;
  setHasFocus: (val: boolean) => void;
  setCurrentPercent: (value: number) => void;
  setIsDragging: (val: boolean) => void;
}) => {
  const pan = useRef(new Animated.Value(0)).current;

  /**
   * We hang onto focus even after our touch ends for design reasons.
   * To enable this, we use a text field to spoof focus inside this element.
   * This also allows us to extend to keyboard functionality fairly easily
   * Note that this is only really relevant for web. On Native we ignore
   * this additional focus issue
   */
  const touchableFocusRef = useRef<TextInput>(null);
  const [hasInternalFocus, setInternalHasFocus] = useState(false);
  const [hasTouchableFocus, setHasTouchableFocus] = useState(false);
  const hasFocus = hasInternalFocus || hasTouchableFocus;

  const [currentPercentValue, setCurrentPercentValue] =
    useState(currentPercent);

  /**
   * Manage our boundaries with refs so that we can update them
   * and maintain them outside react rendering. Necessary for
   * panresponder to eat the new values
   */
  const curLeftBoundaryPercentRef = useRef(leftBoundaryPercent);
  const curRightBoundaryPercentRef = useRef(rightBoundaryPercent);
  useEffect(() => {
    curLeftBoundaryPercentRef.current = leftBoundaryPercent;
    curRightBoundaryPercentRef.current = rightBoundaryPercent;
  }, [leftBoundaryPercent, rightBoundaryPercent]);

  const getPercentFromXValue = useCallback(
    (value: number) => {
      const percent = value ? Math.round((value / sliderPixelWidth) * 100) : 0;
      return percent;
    },
    [sliderPixelWidth]
  );

  /**
   * Update if we are given a new initial percent
   */
  useEffect(() => {
    const currentPosition = getXValueFromPercent(
      currentPercent,
      sliderPixelWidth
    );
    pan.setValue(currentPosition);
  }, [currentPercent, pan, sliderPixelWidth]);

  /**
   * We create a callback for this so that we can regularly recreate it
   * if we need to. If any of the values we need to use to calculate our responses,
   * such as widths etc. change, we create a new handler and jam it on the ref
   */
  const createPanResponder = useCallback<() => PanResponderInstance>(() => {
    return PanResponder.create({
      // Ask to be the responder:
      onStartShouldSetPanResponder: (evt, gestureState) => true,
      onStartShouldSetPanResponderCapture: (evt, gestureState) => true,
      onMoveShouldSetPanResponder: (evt, gestureState) => true,
      onMoveShouldSetPanResponderCapture: (evt, gestureState) => true,

      //On touch/press to start move
      onPanResponderGrant: (evt, gestureState) => {
        //@ts-ignore
        pan.setOffset(pan._value);
        pan.setValue(0);
        setInternalHasFocus(true);
        setHasFocus(true);
        setIsDragging(true);
      },
      //Every tick of move
      onPanResponderMove: (evt, gestureState) => {
        //We add an offset to avoid running on top of the other knob.
        //The offset is the same width as the knob
        const curPositionWithOffset = isRight
          ? //@ts-ignore
            gestureState.dx + pan._offset - 20
          : //@ts-ignore
            gestureState.dx + pan._offset + 20;
        const curPercent = getPercentFromXValue(curPositionWithOffset);
        //We still get the raw percent so that we set the correct percent. Otherwise,
        //we experience "jiggle" from bumping the position slightly for our above check
        //We also need to check the side where we would "bump" with the offset, but not the
        //side that has the end of the line(ie for left we check left raw, right with offset)
        //@ts-ignore
        const rawPercent = getPercentFromXValue(gestureState.dx + pan._offset);
        if (
          ((isRight && curPercent >= curLeftBoundaryPercentRef.current) ||
            (!isRight && rawPercent >= curLeftBoundaryPercentRef.current)) &&
          ((isRight && rawPercent <= curRightBoundaryPercentRef.current) ||
            (!isRight && curPercent <= curRightBoundaryPercentRef.current))
        ) {
          Animated.event([null, { dx: pan }], { useNativeDriver: false })(
            evt,
            gestureState
          );
          //We always set percent, even when moving so that we can update our center
          //bar size
          setCurrentPercent(rawPercent);
          setCurrentPercentValue(rawPercent);
        }
      },
      onPanResponderTerminationRequest: (evt, gestureState) => true,
      //Let go
      onPanResponderRelease: (evt, gestureState) => {
        // The user has released all touches
        pan.flattenOffset();
        setInternalHasFocus(false);
        setIsDragging(false);
        if (Platform.OS === "web") {
          setHasTouchableFocus(true);
        } else {
          setHasFocus(false);
        }

        setTimeout(() => {
          touchableFocusRef.current?.focus();
        }, 0);

        // Calculate the percentage
        //@ts-ignore
        const position = pan._value;
        const percent = getPercentFromXValue(position);

        setCurrentPercent(percent);
        setCurrentPercentValue(percent);
      },
      onPanResponderTerminate: (evt, gestureState) => {
        // Another component has become the responder, so this gesture
        // should be cancelled
        pan.flattenOffset();
        setInternalHasFocus(false);
        setHasFocus(false);
        setIsDragging(false);
      },
    });
  }, [
    getPercentFromXValue,
    isRight,
    pan,
    setCurrentPercent,
    setHasFocus,
    setIsDragging,
  ]);

  const panResponder = React.useRef<PanResponderInstance>(createPanResponder());

  useEffect(() => {
    panResponder.current = createPanResponder();
  }, [createPanResponder]);

  /**
   * Our value might just be the raw percent, or we might have been
   * given a value to show
   */
  const percentString = `${currentPercentValue}%`;
  const toolTipContent = currentValueFromMap
    ? currentValueFromMap.label
      ? currentValueFromMap.label
      : currentValueFromMap.value
    : percentString;

  const [isHovered, setIsHovered] = useState(false);

  const isLarge = isHovered || hasFocus;
  return (
    <Animated.View
      style={{
        transform: [{ translateX: pan }],
        width: isLarge ? 22 : 20,
        height: isLarge ? 22 : 20,
        position: "absolute",
        top: isLarge ? -1 : 0,
        ...(Platform.OS === "web"
          ? {
              // eslint-disable-next-line no-restricted-syntax -- web-only drag cursor hint, guarded by Platform.OS check
              cursor: "pointer",
            }
          : {}),
      }}
    >
      {hasInternalFocus ? <EventCatchingOverlay /> : null}
      {hasFocus ? <ToolTipBox content={toolTipContent} /> : null}
      <Box
        //@ts-ignore
        onMouseEnter={() => {
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
        width={isLarge ? 22 : 20}
        height={isLarge ? 22 : 20}
        style={{ borderRadius: 10 }}
        backgroundColor="surface-primary"
        flexDirection={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        testID={`slider-knob-${isRight ? "right" : "left"}`}
        {...panResponder.current.panHandlers}
      >
        {Platform.OS === "web" ? (
          <TextInput
            ref={touchableFocusRef}
            aria-label={t`slider-knob-${isRight ? "right" : "left"}`}
            onFocus={() => {
              setHasFocus(true);
              setHasTouchableFocus(true);
            }}
            onBlur={() => {
              if (!hasInternalFocus) {
                setHasFocus(false);
              }

              setHasTouchableFocus(false);
            }}
            style={{
              position: "absolute",
              width: 0,
              height: 0,
              opacity: 0,
              fontSize: 16,
            }}
            {...({ tabIndex: -1 } as object)}
          />
        ) : null}

        <MotiView
          animate={{
            width: isLarge ? 20 : 16,
            height: isLarge ? 20 : 16,
            borderRadius: isLarge ? 10 : 8,
          }}
          transition={{
            width: {
              type: "timing",
              duration: AnimationDurations.fast01,
              easing: AnimationEasings.standard,
            },
            height: {
              type: "timing",
              duration: AnimationDurations.fast01,
              easing: AnimationEasings.standard,
            },
            borderRadius: {
              type: "timing",
              duration: AnimationDurations.fast01,
              easing: AnimationEasings.standard,
            },
          }}
          style={{ position: "relative", overflow: "hidden" }}
        >
          <Box
            backgroundColor={hasFocus ? "icon-main-primary" : "black"}
            position="absolute"
            left={0}
            right={0}
            top={0}
            bottom={0}
          />
        </MotiView>
      </Box>
    </Animated.View>
  );
};

/**
 *
 * This exists because the DS tooltip implementation clashes with our animations/movement in
 * this component.
 */
const ToolTipBox = ({ content }: { content: string }) => {
  const numberOfLines = content.length < 32 ? 1 : undefined;
  const [width, setWidth] = useState<null | number>(null);
  const size = content.length > 32 ? "large" : "small";
  return (
    <Box
      backgroundColor="surface-inverse"
      paddingHorizontal={4}
      paddingVertical={2}
      borderRadius={"2"}
      position="absolute"
      top={-40}
      left="50%"
      width={size === "large" ? 200 : content.length * 9 + 32}
      style={{ marginLeft: width ? -width / 2 : 0 }}
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        setWidth(width);
      }}
      opacity={width ? 1 : 0}
      flex={1}
    >
      {width ? (
        <Box
          width={12}
          height={12}
          backgroundColor={"surface-inverse"}
          style={{ transform: [{ rotate: "45deg" }] }}
          position="absolute"
          borderRadius={"0.5"}
          bottom={-6}
          left={width / 2 - 6}
        />
      ) : null}

      <Text
        variant="mainMicro"
        color="grayscale-100"
        numberOfLines={numberOfLines}
        textAlign={"center"}
      >
        {content}
      </Text>
    </Box>
  );
};

/**
 *
 * This guy renders while dragging the slider so that we can avoid accidentally
 * highlighting text during our drag and other odd behavior
 */
const EventCatchingOverlay = () => {
  return (
    <Modal transparent={true} animationType="none" visible={true}>
      <View style={styles.overlay} />
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0)", // Transparent overlay
    zIndex: 999,
  },
});
